// AudioEngine: owns the AudioContext + mic stream, runs the AudioWorklet capture,
// and performs YIN pitch detection on each posted frame. Dispatches analyzed frames
// to subscribers (tuner, practice, timbre modules). Pure-frontend (NFR-4).

import { yinDetect } from './yin';
import { freqToMidi } from './freq';
import type { UserPreferences } from '../types';

export interface AnalyzedFrame {
  time: number;
  samples: Float32Array;
  sampleRate: number;
  rms: number;
  freq: number;
  midi: number;
  confidence: number;
}

export type FrameListener = (frame: AnalyzedFrame) => void;

const WORKLET_URL = '/worklet/pitch-processor.js';

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioWorkletNode | null = null;
  private listeners = new Set<FrameListener>();
  private prefs: UserPreferences;
  private contextResumeBound: () => void;
  private onStateBound: () => void;

  constructor(prefs: UserPreferences) {
    this.prefs = prefs;
    this.contextResumeBound = () => this.handleVisibility();
    this.onStateBound = () => this.handleStateChange();
  }

  updatePreferences(prefs: UserPreferences): void {
    this.prefs = prefs;
  }

  get isRunning(): boolean {
    return this.ctx !== null && this.stream !== null;
  }

  get sampleRate(): number {
    return this.ctx?.sampleRate ?? 48000;
  }

  // Shared AudioContext for playback modules (FR-4/FR-5). Null until start().
  get audioContext(): AudioContext | null {
    return this.ctx;
  }

  // Initialize just the AudioContext (no mic, no permission prompt).
  // Use this for playback-only features like the reference tone so users can
  // hear the target pitch BEFORE granting microphone access. Safe to call
  // multiple times — returns existing ctx if already created.
  async ensureAudio(): Promise<AudioContext | null> {
    if (!this.ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      try {
        this.ctx = new Ctor({ sampleRate: 48000 });
      } catch {
        this.ctx = new Ctor();
      }
      // E-4: AudioContext suspension recovery (visibilitychange + pageshow + statechange).
      document.addEventListener('visibilitychange', this.contextResumeBound);
      window.addEventListener('pageshow', this.contextResumeBound);
      this.ctx.addEventListener('statechange', this.onStateBound);
    }
    if (this.ctx.state === 'suspended') {
      try { await this.ctx.resume(); } catch { /* resume failure surfaced via UI (E-4 mask) */ }
    }
    return this.ctx;
  }

  // Start mic capture. Throws on permission denial (caller handles E-6 guidance).
  async start(): Promise<void> {
    // Secure-context guard: getUserMedia/AudioContext require HTTPS or localhost.
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new TypeError(
        'navigator.mediaDevices is undefined — non-secure context. Use https:// or localhost.',
      );
    }
    const ctx = await this.ensureAudio();
    if (!ctx) throw new TypeError('AudioContext is not supported in this browser.');
    if (this.node) return; // mic already started

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
      },
    });

    await ctx.audioWorklet.addModule(WORKLET_URL);
    this.source = ctx.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(ctx, 'pitch-processor');
    this.node.port.onmessage = (ev) => this.onFrame(ev.data as Float32Array);
    this.source.connect(this.node);
    // Worklet has no output; connect to a zero-gain destination to keep graph alive.
    const sink = ctx.createGain();
    sink.gain.value = 0;
    this.node.connect(sink);
    sink.connect(ctx.destination);
  }

  private onFrame(samples: Float32Array): void {
    if (!this.ctx) return;
    const sr = this.ctx.sampleRate;
    let rms = 0;
    for (let i = 0; i < samples.length; i++) rms += samples[i] * samples[i];
    rms = Math.sqrt(rms / samples.length);

    const { freq, confidence } = yinDetect(samples, sr);
    const midi = freq > 0 ? freqToMidi(freq, this.prefs.A4) : 0;
    const frame: AnalyzedFrame = {
      time: performance.now(),
      samples,
      sampleRate: sr,
      rms,
      freq,
      midi,
      confidence,
    };
    for (const l of this.listeners) l(frame);
  }

  onFrameEvent(cb: FrameListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  // NFR-4: release tracks on stop / page leave.
  stop(): void {
    document.removeEventListener('visibilitychange', this.contextResumeBound);
    window.removeEventListener('pageshow', this.contextResumeBound);
    this.ctx?.removeEventListener('statechange', this.onStateBound);
    this.stream?.getTracks().forEach((t) => t.stop());
    this.node?.disconnect();
    this.source?.disconnect();
    this.ctx?.close();
    this.stream = null;
    this.source = null;
    this.node = null;
    this.ctx = null;
  }

  private async handleVisibility(): Promise<void> {
    if (!this.ctx) return;
    if (document.visibilityState === 'visible' && this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch {
        // resume failure surfaced via UI guidance (E-4 mask) handled by callers
      }
    }
  }

  private handleStateChange(): void {
    // Intentionally minimal: callers can poll isRunning / ctx.state for masking.
  }
}
